import React, { useCallback, useMemo, useState } from "react";
import { useLocation } from "react-router-dom";
import useUserStore from "../stores/useUserStore";
import { apiFetchAuthed } from "../apiHelpers";
import Login from "./Login";
import LoadingIcon from "../LoadingIcon/LoadingIcon";
import { isAllowedPilotOrigin } from "./pilotOriginAllowlist";
import { buttonClass } from "../components/ui/Button";
import { Alert } from "../components/ui/Alert";
import Reveal from "../components/ui/Reveal";

/**
 * /pilot-auth — the popup handoff page for the Firefly Pilot GCS integration.
 *
 * Pilot (a LAN/VPN origin) can't read firefleet's httpOnly refresh cookie, so it
 * opens this firefleet-origin popup. Here we:
 *   1. Piggyback on the app's silent refresh (App mount runs `initializeAuth`,
 *      which does POST /login/refresh) → read the resulting session from the store.
 *   2. If not logged in → render firefleet's own <Login> inline (reused, its
 *      post-login navigation suppressed so we stay on /pilot-auth).
 *   3. If logged in → show an explicit "Authorize Firefly Pilot" consent screen
 *      that names the requesting pilot origin.
 *   4. On confirm → POST /api/auth/pilot-token (Authorization from the logged-in
 *      firefleet session) → postMessage the token pair to `window.opener` at the
 *      EXACT validated pilot origin (never '*') → close.
 *
 * The requesting origin (`?origin=`) is validated against pilotOriginAllowlist
 * BEFORE anything else — a disallowed origin is refused and no tokens are minted.
 */
const PilotAuth = () => {
  const { search } = useLocation();
  const user = useUserStore((s) => s.user);
  const isInitializing = useUserStore((s) => s.isInitializing);

  const pilotOrigin = useMemo(
    () => new URLSearchParams(search).get("origin") || "",
    [search]
  );
  const originOk = useMemo(() => isAllowedPilotOrigin(pilotOrigin), [pilotOrigin]);

  const [phase, setPhase] = useState("idle"); // idle | authorizing | error
  const [errorMsg, setErrorMsg] = useState(null);

  const handleAuthorize = useCallback(async () => {
    setPhase("authorizing");
    setErrorMsg(null);
    try {
      // Re-validate at the moment of use (defense in depth — never post to an
      // origin that isn't currently allowlisted).
      if (!isAllowedPilotOrigin(pilotOrigin)) {
        throw new Error("Requesting origin is not allowed.");
      }
      if (!window.opener || window.opener.closed) {
        throw new Error(
          "No opener window. Open this page from Firefly Pilot, not directly."
        );
      }

      // Minted as the logged-in firefleet user (Authorization injected by
      // apiFetchAuthed from the in-memory firefleet-app access token).
      const tokens = await apiFetchAuthed("/auth/pilot-token", {
        method: "POST",
        silent: true,
      });

      // CRITICAL: target the exact validated origin, NEVER '*'.
      window.opener.postMessage(
        { type: "firefleet-pilot-auth", tokens },
        pilotOrigin
      );
      window.close();
    } catch (err) {
      setPhase("error");
      setErrorMsg(err?.message || "Failed to authorize Firefly Pilot.");
    }
  }, [pilotOrigin]);

  // ── 1) Refuse a disallowed / missing origin up front — before session checks
  //       or token minting. Fails closed.
  if (!originOk) {
    return (
      <Shell title="Firefly Pilot — Authorization" tone="danger">
        <p className="mb-3 text-red-300">
          This request can’t be authorized.
        </p>
        <p className="mb-4 text-sm text-on-surface">
          The requesting origin is missing or not on the allowlist, so no
          Firefleet tokens will be issued.
        </p>
        {pilotOrigin ? (
          <OriginBadge origin={pilotOrigin} tone="danger" />
        ) : (
          <p className="text-sm text-on-surface-variant">No <code>origin</code> was provided.</p>
        )}
        <p className="mt-4 text-xs text-on-surface-variant">
          You can close this window.
        </p>
      </Shell>
    );
  }

  // ── 2) Session check still in flight (App-level silent refresh).
  if (isInitializing) {
    return (
      <Shell title="Firefly Pilot — Authorization">
        <LoadingIcon />
        <p className="mt-2 text-center text-sm text-on-surface-variant">
          Checking your Firefleet session…
        </p>
      </Shell>
    );
  }

  // ── 3) Not logged into firefleet → reuse the existing Login form inline.
  //       onAuthenticated suppresses Login's history.push("/"); the store update
  //       (user.loggedin → true) re-renders this component into the consent step.
  //       Login is self-contained (renders its own card), so we sit a slim
  //       context banner above it rather than nesting it in <Shell>.
  if (!user.loggedin) {
    return (
      <div
        className="min-h-screen bg-[#191919] pt-10 pb-16"
        style={{ fontFamily: "RobotoMono, sans-serif" }}
      >
        <div className="w-11/12 max-w-md mx-auto text-center text-white">
          <h3 className="text-lg font-bold">Firefly Pilot wants to connect</h3>
          <p className="text-sm text-on-surface-variant mt-1 mb-4">
            Sign in to Firefleet to continue.
          </p>
          <OriginBadge origin={pilotOrigin} />
        </div>
        <Login onAuthenticated={() => { /* store re-render advances to consent */ }} />
      </div>
    );
  }

  // ── 4) Logged in → explicit consent.
  return (
    <Shell title="Authorize Firefly Pilot">
      <p className="mb-4 text-sm text-on-surface">
        Firefly Pilot wants to act on your behalf in Firefleet as{" "}
        <span className="font-semibold text-white">{user.email}</span>. It will
        be able to read and write Firefleet data your account can access (flags,
        workflow status, firmware labels).
      </p>

      <OriginBadge origin={pilotOrigin} />

      <Reveal show={phase === "error"}>
        <Alert className="mt-4">{errorMsg}</Alert>
      </Reveal>

      <button
        type="button"
        onClick={handleAuthorize}
        disabled={phase === "authorizing"}
        className={`${buttonClass("filled", "lg")} mt-5 w-full`}
      >
        {phase === "authorizing" ? "Authorizing…" : "Authorize Firefly Pilot"}
      </button>

      <button
        type="button"
        onClick={() => window.close()}
        className={`${buttonClass("quiet", "lg")} mt-2 w-full`}
      >
        Cancel
      </button>
    </Shell>
  );
};

/** Themed card matching firefleet's dark login chrome. */
const Shell = ({ title, subtitle, tone, children }) => (
  <div
    className="flex items-center justify-center min-h-screen px-4 bg-[#191919]"
    style={{ fontFamily: "RobotoMono, sans-serif" }}
  >
    <div
      className={`w-11/12 max-w-md p-6 bg-[#2a2a2a] text-white rounded-lg shadow-lg
                  border-2 ${tone === "danger" ? "border-red-800" : "border-black"}`}
    >
      <h3 className="text-center text-lg font-bold mb-2">{title}</h3>
      {subtitle && (
        <p className="text-center text-sm text-on-surface-variant mb-5">{subtitle}</p>
      )}
      {children}
    </div>
  </div>
);

/** Shows the exact origin we would post tokens to — the trust anchor the user confirms. */
const OriginBadge = ({ origin, tone }) => (
  <div
    className={`px-3 py-2 rounded border text-sm break-all ${
      tone === "danger"
        ? "bg-[#3a3a3a] border-red-800 text-red-200"
        : "bg-[#3a3a3a] border-outline-variant text-on-surface"
    }`}
  >
    <span className="block text-xs text-on-surface-variant mb-1">Requesting origin</span>
    <code>{origin}</code>
  </div>
);

export default PilotAuth;
